import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getSyncEvent, getSessionEvents, getSyncEvents } from '../api';
import { formatTimestamp, formatRelativeTime, getEventTypeLabel, getEventTypeIcon, formatFileSize, formatDuration } from '../utils';
import { parseCorruptionDetails, getCorruptionSeverity, getSeverityColor } from '../utils/corruption';
import { getClientIpFromHeaders, isDeviceLinkConflictRequestSummary } from '../utils/deviceLinkConflict';
import type { 
  SyncEvent, 
  NameDeduplicationDetail,
  ConflictCreatedResponseSummary,
  ConflictResolvedResponseSummary,
  DeviceLinkConflictResponseSummary,
  ConflictAction,
  FileUploadRequestSummary,
  SuspiciousFileName
} from '../types';
import { QUARANTINE_REASON_LABELS, EVENT_TYPES, CONFLICT_ACTION_LABELS } from '../types';
import styles from './EventDetail.module.css';

/**
 * Format raw bytes diagnostic for copy
 */
function formatRawBytesDiagnostic(event: SyncEvent): string {
  const uploadRequest = event.request_summary as FileUploadRequestSummary | undefined;
  const diagnostic = uploadRequest?.upload_validation?.raw_bytes_diagnostic;
  
  if (!diagnostic) return '';
  
  return `
RAW BYTES DIAGNOSTIC
--------------------
Null Byte Count: ${diagnostic.null_byte_count.toLocaleString()}
Null Percentage: ${diagnostic.null_percentage.toFixed(1)}%
First 200 (readable): ${diagnostic.first_200_readable || '(empty)'}
Last 200 (readable): ${diagnostic.last_200_readable || '(empty)'}
`;
}

/**
 * Event Detail Page
 * 
 * Shows complete details for a single sync event including
 * content, request/response data, and corruption info.
 */

export default function EventDetail() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState<SyncEvent | null>(null);
  const [sessionEvents, setSessionEvents] = useState<SyncEvent[]>([]);
  const [prevEventId, setPrevEventId] = useState<string | null>(null);
  const [nextEventId, setNextEventId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'content' | 'request' | 'session'>('overview');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [isCopyingSession, setIsCopyingSession] = useState(false);
  const [isHeadersExpanded, setIsHeadersExpanded] = useState(false);
  const [isDiagnosticExpanded, setIsDiagnosticExpanded] = useState(false);
  const [isHexExpanded, setIsHexExpanded] = useState(false);

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  useEffect(() => {
    async function fetchData() {
      if (!eventId) return;
      
      setIsLoading(true);
      setError(null);
      setPrevEventId(null);
      setNextEventId(null);

      try {
        const eventData = await getSyncEvent(eventId);
        setEvent(eventData);

        // Also fetch other events in the same session
        if (eventData.session_id) {
          const sessionData = await getSessionEvents(eventData.session_id).catch(() => []);
          setSessionEvents(Array.isArray(sessionData) ? sessionData : (sessionData as any).events || []);
        }

        // Fetch adjacent events for prev/next navigation
        const timestamp = eventData.timestamp;
        
        // Get previous event (older)
        const prevResponse = await getSyncEvents({
          timestamp_lte: timestamp,
          ordering: '-timestamp',
          page_size: 2,
        }).catch(() => ({ results: [] }));
        
        // Find an event that's not the current one
        const prevEvent = prevResponse.results?.find(e => e.id !== eventId);
        if (prevEvent) setPrevEventId(prevEvent.id);

        // Get next event (newer)
        const nextResponse = await getSyncEvents({
          timestamp_gte: timestamp,
          ordering: 'timestamp',
          page_size: 2,
        }).catch(() => ({ results: [] }));
        
        const nextEvent = nextResponse.results?.find(e => e.id !== eventId);
        if (nextEvent) setNextEventId(nextEvent.id);

      } catch (err) {
        console.error('[EventDetail] Error:', err);
        setError('Failed to load event details');
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [eventId]);

  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading event details...</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className={styles.page}>
        <div className={styles.error}>
          {error || 'Event not found'}
          <button onClick={() => navigate('/events')} className={styles.backButton}>
            ← Back to Events
          </button>
        </div>
      </div>
    );
  }

  const findings = parseCorruptionDetails(event.suspicious_details);
  const severity = event.has_suspicious_chars 
    ? getCorruptionSeverity(findings, event.suspicious_char_count)
    : null;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.navRow}>
          <button onClick={() => navigate(-1)} className={styles.backButton}>
            ← Back
          </button>
          <div className={styles.eventNav}>
            <button 
              onClick={() => prevEventId && navigate(`/events/${prevEventId}`)}
              disabled={!prevEventId}
              className={styles.navButton}
              title="Previous event (older)"
            >
              ← Prev
            </button>
            <button 
              onClick={() => nextEventId && navigate(`/events/${nextEventId}`)}
              disabled={!nextEventId}
              className={styles.navButton}
              title="Next event (newer)"
            >
              Next →
            </button>
          </div>
        </div>
        <div className={styles.headerContent}>
          <div className={styles.headerMain}>
            <span className={styles.eventIcon}>{getEventTypeIcon(event.event_type)}</span>
            <h1 className={styles.title}>{getEventTypeLabel(event.event_type)}</h1>
            {event.was_quarantined && (
              <span className={
                event.quarantine_reason === 'SEVERE_TRUNCATION' ? styles.truncationBadge : 
                event.quarantine_reason === 'EMPTY_UPLOAD' ? styles.emptyUploadBadge :
                styles.quarantinedBadge
              }>
                {event.quarantine_reason === 'SEVERE_TRUNCATION' ? '✂️ TRUNCATED' : 
                 event.quarantine_reason === 'EMPTY_UPLOAD' ? '📭 EMPTY UPLOAD' :
                 '🔒 QUARANTINED'}
              </span>
            )}
            {event.has_suspicious_chars && severity && !event.was_quarantined && (
              <span 
                className={styles.severityBadge}
                style={{ backgroundColor: `${getSeverityColor(severity)}20`, color: getSeverityColor(severity) }}
              >
                ⚠️ {severity.toUpperCase()}
              </span>
            )}
            {(() => {
              const uploadReq = event.request_summary as FileUploadRequestSummary | undefined;
              const suspicious = uploadReq?.upload_validation?.suspicious_file_name;
              if (!suspicious) return null;
              return (
                <span className={styles.suspiciousNameHeaderBadge} title={suspicious.description}>
                  🏷️ SUSPICIOUS NAME
                </span>
              );
            })()}
            {!event.success && (
              <span className={styles.failedBadge}>❌ FAILED</span>
            )}
          </div>
          <p className={styles.timestamp}>
            {formatTimestamp(event.timestamp)} ({formatRelativeTime(event.timestamp)})
          </p>
        </div>
      </div>

      <div className={styles.tabs}>
        <button 
          className={`${styles.tab} ${activeTab === 'overview' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'content' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('content')}
        >
          Content
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'request' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('request')}
        >
          Request/Response
        </button>
        <button 
          className={`${styles.tab} ${activeTab === 'session' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('session')}
        >
          Session ({sessionEvents.length})
        </button>
      </div>

      <div className={styles.content}>
        {activeTab === 'overview' && (
          <div className={styles.overviewGrid}>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Event Info</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Event ID</span>
                  <span className={styles.infoValue}>{event.id}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Session ID</span>
                  <span className={styles.infoValue}>{event.session_id}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Event Type</span>
                  <span className={styles.infoValue}>{event.event_type}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Status Code</span>
                  <span className={styles.infoValue}>{event.status_code ?? '-'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Duration</span>
                  <span className={styles.infoValue}>{formatDuration(event.duration_ms)}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>First Sync</span>
                  <span className={styles.infoValue}>{event.is_first_sync ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>User & Device</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>User Email</span>
                  {event.user_email ? (
                    <Link 
                      to={`/events?user_email=${encodeURIComponent(event.user_email)}`}
                      className={styles.infoLink}
                    >
                      {event.user_email}
                    </Link>
                  ) : (
                    <span className={styles.infoValue}>-</span>
                  )}
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Device Code</span>
                  {event.device_code ? (
                    <Link 
                      to={`/events?device_code=${encodeURIComponent(event.device_code)}`}
                      className={styles.infoLinkMono}
                    >
                      {event.device_code}
                    </Link>
                  ) : (
                    <span className={styles.infoValueMono}>-</span>
                  )}
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Device Name</span>
                  <span className={styles.infoValue}>{event.device_name || '-'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Firmware</span>
                  <span className={styles.infoValue}>{event.firmware_version || '-'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Pico Version</span>
                  <span className={styles.infoValue}>{event.pico_version || '-'}</span>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>File Info</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>File Name</span>
                  <span className={styles.infoValue}>{event.file_name || '-'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>File ID</span>
                  <span className={styles.infoValue}>{event.file_id || '-'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>File Type</span>
                  <span className={styles.infoValue}>{event.file_type || '-'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>File Size</span>
                  <span className={styles.infoValue}>{formatFileSize(event.file_size_bytes)}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Project</span>
                  <span className={styles.infoValue}>{event.project_name || '-'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Project ID</span>
                  <span className={styles.infoValue}>{event.project_id || '-'}</span>
                </div>
              </div>
            </div>

            {/* Suspicious File Name Warning */}
            {(() => {
              const uploadReq = event.request_summary as FileUploadRequestSummary | undefined;
              const suspicious: SuspiciousFileName | null | undefined = uploadReq?.upload_validation?.suspicious_file_name;
              if (!suspicious) return null;
              return (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>🏷️ Suspicious File Name</h3>
                  <div className={styles.suspiciousNameInfo}>
                    <div className={styles.suspiciousNameAlert}>
                      <span className={styles.suspiciousNameIcon}>🏷️</span>
                      <div className={styles.suspiciousNameContent}>
                        <div className={styles.suspiciousNameDescription}>{suspicious.description}</div>
                        <div className={styles.suspiciousNameMeta}>
                          <span className={styles.suspiciousNameLabel}>File Name:</span>
                          <code className={styles.suspiciousNameValue}>
                            {suspicious.file_name ? `"${suspicious.file_name}"` : '(empty)'}
                          </code>
                          <span className={styles.suspiciousNameSeparator}>|</span>
                          <span className={styles.suspiciousNameLabel}>Issue:</span>
                          <code className={styles.suspiciousNameValue}>{suspicious.issue}</code>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {event.was_quarantined && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>🔒 Quarantined</h3>
                <div className={styles.quarantineInfo}>
                  <p className={styles.quarantineExplainer}>
                    This upload was quarantined to protect user data. The corrupted content was NOT saved to the file.
                  </p>
                  <div className={styles.infoGrid}>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Quarantine Reason</span>
                      <span className={styles.infoValue}>
                        {event.quarantine_reason ? QUARANTINE_REASON_LABELS[event.quarantine_reason] : 'Unknown'}
                      </span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Reason Code</span>
                      <span className={styles.infoValueMono}>{event.quarantine_reason || '-'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Upload Validation & Raw Bytes Diagnostic for file uploads */}
            {(() => {
              const uploadRequest = event.request_summary as FileUploadRequestSummary | undefined;
              const validation = uploadRequest?.upload_validation;
              const diagnostic = validation?.raw_bytes_diagnostic;
              
              // Show section if we have any upload validation data
              if (!validation) return null;
              
              const hasDiagnostic = !!diagnostic;
              const isHighNull = hasDiagnostic && diagnostic.null_percentage > 50;
              const isSuspicious = hasDiagnostic && diagnostic.null_percentage > 10;
              
              // Determine diagnosis based on documentation patterns:
              // - Nulls at front, text at end → Device offset bug
              // - Text at front, nulls at end → Device buffer overread
              // - No nulls, all readable text → Healthy upload OR server stripping issue
              // - High null % + quarantined → Firmware bug confirmed
              const getDiagnosisHint = (): string | null => {
                if (!hasDiagnostic || !diagnostic) return null;
                
                const firstHasManyDots = diagnostic.first_200_readable?.match(/^\.{10,}/) != null;
                const lastHasManyDots = diagnostic.last_200_readable?.match(/\.{10,}$/) != null;
                
                if (isHighNull && event.was_quarantined) {
                  return 'Firmware bug confirmed - high null percentage in quarantined upload';
                }
                if (firstHasManyDots && !lastHasManyDots) {
                  return 'Device offset bug - reading from wrong file position';
                }
                if (!firstHasManyDots && lastHasManyDots) {
                  return 'Device buffer overread - not tracking bytes read';
                }
                if (isHighNull) {
                  return 'High null byte content detected - likely firmware issue';
                }
                if (isSuspicious) {
                  return 'Suspicious null content - investigate further';
                }
                if (diagnostic.null_percentage === 0) {
                  return 'Healthy upload - no null bytes detected';
                }
                return null;
              };
              const diagnosisHint = getDiagnosisHint();
              
              // Check for size mismatch
              const hasSizeMismatch = validation.size_mismatch === true;
              const validationPassed = validation.validation_passed === true;
              
              return (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    {hasDiagnostic ? '🔬 Upload Validation & Byte Diagnostic' : '📊 Upload Validation'}
                    {hasDiagnostic && (
                      <span 
                        className={styles.helpIcon}
                        title="Interpretation Guide:&#10;&#10;• Nulls at front, text at end → Device offset bug (reading from wrong position)&#10;• Text at front, nulls at end → Device buffer overread&#10;• No nulls, all readable → Healthy upload&#10;• High null % + quarantined → Firmware bug confirmed&#10;&#10;The readable previews use '.' to represent null/non-printable bytes"
                      >
                        ❓
                      </span>
                    )}
                  </h3>
                  <div className={styles.diagnosticInfo}>
                    {/* Raw bytes diagnostic - prominent null percentage (only if diagnostic exists) */}
                    {hasDiagnostic && diagnostic && (
                      <div className={styles.nullPercentageDisplay}>
                        <div className={`${styles.nullPercentageValue} ${isHighNull ? styles.nullPercentageHigh : styles.nullPercentageLow}`}>
                          {diagnostic.null_percentage.toFixed(1)}%
                        </div>
                        <div className={styles.nullPercentageLabel}>
                          Null Bytes ({diagnostic.null_byte_count.toLocaleString()} total)
                        </div>
                        {diagnosisHint && (
                          <div className={styles.diagnosisHint}>
                            💡 {diagnosisHint}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Validation stats - always show if available */}
                    <div className={styles.validationStats}>
                      {validation.validation_passed !== undefined && (
                        <div className={styles.validationStat}>
                          <span className={styles.validationStatLabel}>Validation Status</span>
                          <span className={`${styles.validationStatValue} ${validationPassed ? styles.validationPassed : styles.validationFailed}`}>
                            {validationPassed ? '✓ Passed' : '✗ Failed'}
                          </span>
                        </div>
                      )}
                      {validation.size_mismatch !== undefined && (
                        <div className={styles.validationStat}>
                          <span className={styles.validationStatLabel}>Size Mismatch</span>
                          <span className={`${styles.validationStatValue} ${hasSizeMismatch ? styles.validationFailed : ''}`}>
                            {hasSizeMismatch ? '⚠️ Yes' : 'No'}
                          </span>
                        </div>
                      )}
                      {validation.content_length_header != null && (
                        <div className={styles.validationStat}>
                          <span className={styles.validationStatLabel}>Content-Length Header</span>
                          <span className={styles.validationStatValue}>
                            {validation.content_length_header.toLocaleString()} bytes
                          </span>
                        </div>
                      )}
                      {validation.actual_bytes_received != null && (
                        <div className={styles.validationStat}>
                          <span className={styles.validationStatLabel}>Actual Bytes Received</span>
                          <span className={styles.validationStatValue}>
                            {validation.actual_bytes_received.toLocaleString()} bytes
                          </span>
                        </div>
                      )}
                      {validation.multipart_overhead != null && (
                        <div className={styles.validationStat}>
                          <span className={styles.validationStatLabel}>Multipart Overhead</span>
                          <span className={styles.validationStatValue}>
                            {validation.multipart_overhead.toLocaleString()} bytes
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Readable text previews (collapsible) - only if diagnostic exists */}
                    {hasDiagnostic && diagnostic && (
                      <>
                        <div 
                          className={styles.collapsibleHeader}
                          onClick={() => setIsDiagnosticExpanded(!isDiagnosticExpanded)}
                        >
                          <h4 className={styles.subsectionTitle}>
                            <span className={styles.collapseIcon}>{isDiagnosticExpanded ? '▼' : '▶'}</span>
                            Readable Content Preview
                          </h4>
                        </div>
                        {isDiagnosticExpanded && (
                          <div className={styles.diagnosticPreview}>
                            <div className={styles.diagnosticBlock}>
                              <div className={styles.diagnosticBlockLabel}>First 200 bytes (readable)</div>
                              <pre className={styles.diagnosticPre}>{diagnostic.first_200_readable || '(empty)'}</pre>
                            </div>
                            <div className={styles.diagnosticBlock}>
                              <div className={styles.diagnosticBlockLabel}>Last 200 bytes (readable)</div>
                              <pre className={styles.diagnosticPre}>{diagnostic.last_200_readable || '(empty)'}</pre>
                            </div>
                          </div>
                        )}

                        {/* Hex dumps (collapsible) */}
                        <div 
                          className={styles.collapsibleHeader}
                          onClick={() => setIsHexExpanded(!isHexExpanded)}
                        >
                          <h4 className={styles.subsectionTitle}>
                            <span className={styles.collapseIcon}>{isHexExpanded ? '▼' : '▶'}</span>
                            Hex Dump
                          </h4>
                        </div>
                        {isHexExpanded && (
                          <div className={styles.diagnosticPreview}>
                            <div className={styles.diagnosticBlock}>
                              <div className={styles.diagnosticBlockLabel}>First 200 bytes (hex)</div>
                              <pre className={styles.diagnosticPreHex}>{diagnostic.first_200_hex || '(empty)'}</pre>
                            </div>
                            <div className={styles.diagnosticBlock}>
                              <div className={styles.diagnosticBlockLabel}>Last 200 bytes (hex)</div>
                              <pre className={styles.diagnosticPreHex}>{diagnostic.last_200_hex || '(empty)'}</pre>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })()}

            {event.has_suspicious_chars && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>⚠️ Corruption Detected</h3>
                <div className={styles.corruptionInfo}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Suspicious Characters</span>
                    <span className={styles.infoValue}>{event.suspicious_char_count?.toLocaleString()}</span>
                  </div>
                  <div className={styles.findingsRow}>
                    {findings.map((finding, idx) => (
                      <span key={idx} className={styles.findingTag}>{finding.category}</span>
                    ))}
                  </div>
                  <div className={styles.detailsBox}>
                    <pre>{event.suspicious_details}</pre>
                  </div>
                </div>
              </div>
            )}

            {event.error_message && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>❌ Error Message</h3>
                <div className={styles.errorBox}>
                  <pre>{event.error_message}</pre>
                </div>
              </div>
            )}

            {event.has_name_deduplication && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>🔀 Name Deduplication</h3>
                <div className={styles.deduplicationInfo}>
                  {event.is_mass_deduplication && (
                    <div className={styles.deduplicationAlert}>
                      <span className={styles.alertIcon}>🚨</span>
                      <span className={styles.alertText}>
                        <strong>Mass Deduplication Detected</strong> — Unusually high number of files were renamed
                      </span>
                    </div>
                  )}
                  <div className={styles.deduplicationStats}>
                    <div className={styles.deduplicationStat}>
                      <span className={styles.deduplicationStatValue}>
                        {event.name_deduplication_count ?? 0}
                      </span>
                      <span className={styles.deduplicationStatLabel}>Files Renamed</span>
                    </div>
                    <div className={styles.deduplicationStat}>
                      <span className={styles.deduplicationStatValue}>
                        {event.name_deduplication_total_files ?? '-'}
                      </span>
                      <span className={styles.deduplicationStatLabel}>Total Files</span>
                    </div>
                    <div className={styles.deduplicationStat}>
                      <span className={styles.deduplicationStatValue}>
                        {event.name_deduplication_count && event.name_deduplication_total_files
                          ? Math.round((event.name_deduplication_count / event.name_deduplication_total_files) * 100)
                          : 0}%
                      </span>
                      <span className={styles.deduplicationStatLabel}>Rename Rate</span>
                    </div>
                  </div>
                  {event.name_deduplication_details && event.name_deduplication_details.length > 0 && (
                    <div className={styles.deduplicationTable}>
                      <div className={styles.deduplicationTableHeader}>
                        <span>Original Name</span>
                        <span>New Name</span>
                        <span>File ID</span>
                      </div>
                      {event.name_deduplication_details.map((detail: NameDeduplicationDetail, idx: number) => (
                        <div key={idx} className={styles.deduplicationTableRow}>
                          <span className={styles.originalName}>{detail.original_name}</span>
                          <span className={styles.deduplicatedName}>{detail.deduplicated_name}</span>
                          <span className={styles.fileId} title={detail.file_id}>
                            {detail.file_id.slice(0, 12)}...
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {event.event_type === EVENT_TYPES.CONFLICT_CREATED && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>⚡ Conflict Created</h3>
                <div className={styles.conflictInfo}>
                  <div className={styles.conflictBadge}>
                    <span className={styles.conflictBadgeIcon}>⚡</span>
                    <span className={styles.conflictBadgeText}>Version Conflict Detected</span>
                  </div>
                  {(() => {
                    const response = event.response_summary as ConflictCreatedResponseSummary | undefined;
                    return response?.reason ? (
                      <div className={styles.conflictReason}>
                        <div className={styles.conflictReasonLabel}>Reason</div>
                        <div className={styles.conflictReasonText}>{response.reason}</div>
                      </div>
                    ) : null;
                  })()}
                  <div className={styles.infoGrid}>
                    {(() => {
                      const response = event.response_summary as ConflictCreatedResponseSummary | undefined;
                      return (
                        <>
                          {response?.conflict_id && (
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>Conflict ID</span>
                              <span className={styles.infoValueMono}>{response.conflict_id}</span>
                            </div>
                          )}
                          {response?.server_file_id && (
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>Server File ID</span>
                              <span className={styles.infoValueMono}>{response.server_file_id}</span>
                            </div>
                          )}
                          {response?.device_file_id && (
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>Device File ID</span>
                              <span className={styles.infoValueMono}>{response.device_file_id}</span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {event.event_type === EVENT_TYPES.DEVICE_LINK_CONFLICT && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>⚠️ Device Link Conflict</h3>
                <div className={styles.conflictInfo}>
                  <div className={styles.conflictBadge}>
                    <span className={styles.conflictBadgeIcon}>⚠️</span>
                    <span className={styles.conflictBadgeText}>
                      Possible device ID collision — Studio link blocked (409)
                    </span>
                  </div>
                  {isDeviceLinkConflictRequestSummary(event.request_summary) ? (
                    <div className={styles.infoGrid}>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Device ID</span>
                        <span className={styles.infoValueMono}>
                          {event.request_summary.device_id}
                        </span>
                      </div>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Existing owner</span>
                        <span className={styles.infoValue}>
                          {event.request_summary.existing_owner_email}
                        </span>
                      </div>
                      <div className={styles.infoItem}>
                        <span className={styles.infoLabel}>Attempted by</span>
                        <span className={styles.infoValue}>
                          {event.request_summary.attempted_by_email}
                        </span>
                      </div>
                      {getClientIpFromHeaders(event.request_headers) && (
                        <div className={styles.infoItem}>
                          <span className={styles.infoLabel}>Client IP</span>
                          <span className={styles.infoValueMono}>
                            {getClientIpFromHeaders(event.request_headers)}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className={styles.conflictReasonText}>
                      List view may omit owner emails — see Request tab for full summary.
                    </p>
                  )}
                  {(() => {
                    const response = event.response_summary as
                      | DeviceLinkConflictResponseSummary
                      | undefined;
                    if (response && !response.blocked) {
                      return (
                        <p className={styles.conflictReasonText} style={{ marginTop: '0.75rem' }}>
                          Sync was not stopped ({response.status}).
                        </p>
                      );
                    }
                    return null;
                  })()}
                  {event.device_code && (
                    <div style={{ marginTop: '1rem' }}>
                      <Link
                        to={`/device-link-conflicts`}
                        className={styles.viewButton}
                        style={{ marginRight: '0.5rem' }}
                      >
                        ID Collisions dashboard →
                      </Link>
                      <Link
                        to={`/events?device_code=${encodeURIComponent(event.device_code)}`}
                        className={styles.viewButton}
                      >
                        Device event history →
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            )}

            {event.event_type === EVENT_TYPES.CONFLICT_RESOLVED && (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>✅ Conflict Resolved</h3>
                <div className={styles.conflictInfo}>
                  {(() => {
                    const response = event.response_summary as ConflictResolvedResponseSummary | undefined;
                    const actionLabel = response?.action_taken 
                      ? CONFLICT_ACTION_LABELS[response.action_taken as ConflictAction] || response.action_taken
                      : 'Unknown';
                    const actionIcon = response?.action_taken === 'overwrite_server' ? '📱→☁️' 
                      : response?.action_taken === 'keep_both' ? '📱+☁️' 
                      : response?.action_taken === 'discard_device' ? '☁️→📱' 
                      : '❓';
                    return (
                      <>
                        <div className={`${styles.conflictBadge} ${styles.conflictBadgeResolved}`}>
                          <span className={styles.conflictBadgeIcon}>✅</span>
                          <span className={styles.conflictBadgeText}>Conflict Successfully Resolved</span>
                        </div>
                        <div className={styles.conflictResolution}>
                          <span className={styles.conflictResolutionIcon}>{actionIcon}</span>
                          <div className={styles.conflictResolutionInfo}>
                            <span className={styles.conflictResolutionAction}>{actionLabel}</span>
                            {response?.outcome && (
                              <span className={styles.conflictResolutionOutcome}>{response.outcome}</span>
                            )}
                          </div>
                        </div>
                        <div className={styles.infoGrid}>
                          {response?.server_file_id && (
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>Server File ID</span>
                              <span className={styles.infoValueMono}>{response.server_file_id}</span>
                            </div>
                          )}
                          {response?.device_file_id && (
                            <div className={styles.infoItem}>
                              <span className={styles.infoLabel}>Device File ID</span>
                              <span className={styles.infoValueMono}>{response.device_file_id}</span>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'content' && (
          <div className={styles.contentTab}>
            <div className={styles.copyAllRow}>
              <button 
                className={styles.copyAllButton}
                onClick={() => {
                  const quarantineSection = event.was_quarantined 
                    ? `
🔒 QUARANTINED
==============
Quarantine Reason: ${event.quarantine_reason ? QUARANTINE_REASON_LABELS[event.quarantine_reason] : 'Unknown'}
Reason Code: ${event.quarantine_reason || '-'}
Note: This upload was quarantined - corrupted content was NOT saved to protect user data.
` : '';

                  const contentText = `CONTENT INFO
============
File ID: ${event.file_id || '-'}
Content Size (Raw): ${event.content_size_raw?.toLocaleString() ?? '-'} bytes
Encoding: ${event.content_encoding || '-'}
Hash (Raw): ${event.content_hash_raw || '-'}
Hash (Processed): ${event.content_hash_processed || '-'}
Was Quarantined: ${event.was_quarantined ? 'Yes' : 'No'}
${quarantineSection}
RAW CONTENT (FROM DEVICE)
=========================
${event.content_raw || '(No raw content)'}

PROCESSED CONTENT (AFTER SERVER PROCESSING)
============================================
${event.content_processed || '(No processed content)'}`;
                  copyToClipboard(contentText, 'all');
                }}
              >
                {copiedField === 'all' ? '✓ Copied All!' : '📋 Copy All Content'}
              </button>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Content Info</h3>
              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>File ID</span>
                  <span className={styles.infoValueMono}>{event.file_id || '-'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Content Size (Raw)</span>
                  <span className={styles.infoValue}>{event.content_size_raw?.toLocaleString() ?? '-'} bytes</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Encoding</span>
                  <span className={styles.infoValue}>{event.content_encoding || '-'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Hash (Raw)</span>
                  <span className={styles.infoValueMono}>{event.content_hash_raw || '-'}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Hash (Processed)</span>
                  <span className={styles.infoValueMono}>{event.content_hash_processed || '-'}</span>
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Raw Content (from device)</h3>
                {event.content_raw && (
                  <button 
                    className={styles.copyButton}
                    onClick={() => copyToClipboard(event.content_raw!, 'raw')}
                  >
                    {copiedField === 'raw' ? '✓ Copied!' : '📋 Copy'}
                  </button>
                )}
              </div>
              {event.content_raw ? (
                <div className={styles.contentBox}>
                  <pre>{event.content_raw}</pre>
                </div>
              ) : (
                <p className={styles.noContent}>No raw content captured for this event type.</p>
              )}
            </div>

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Processed Content (after server processing)</h3>
                {event.content_processed && (
                  <button 
                    className={styles.copyButton}
                    onClick={() => copyToClipboard(event.content_processed!, 'processed')}
                  >
                    {copiedField === 'processed' ? '✓ Copied!' : '📋 Copy'}
                  </button>
                )}
              </div>
              {event.content_processed ? (
                <div className={styles.contentBox}>
                  <pre>{event.content_processed}</pre>
                </div>
              ) : (
                <p className={styles.noContent}>No processed content available.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'request' && (
          <div className={styles.requestTab}>
            <div className={styles.copyAllRow}>
              <button 
                className={styles.copyAllButton}
                onClick={() => {
                  const quarantineNote = event.was_quarantined 
                    ? `
🔒 QUARANTINE STATUS
====================
Was Quarantined: Yes
Reason: ${event.quarantine_reason ? QUARANTINE_REASON_LABELS[event.quarantine_reason] : 'Unknown'}
` : '';

                  const headersText = event.request_headers && Object.keys(event.request_headers).length > 0
                    ? `
REQUEST HEADERS
===============
${Object.entries(event.request_headers).map(([k, v]) => `${k}: ${v}`).join('\n')}
` : '';

                  const requestText = `ENDPOINT
========
${event.endpoint || 'Not recorded'}
${quarantineNote}${headersText}
REQUEST SUMMARY
===============
${event.request_summary ? JSON.stringify(event.request_summary, null, 2) : '(No request summary)'}

RESPONSE SUMMARY
================
${event.response_summary ? JSON.stringify(event.response_summary, null, 2) : '(No response summary)'}`;
                  copyToClipboard(requestText, 'allRequest');
                }}
              >
                {copiedField === 'allRequest' ? '✓ Copied All!' : '📋 Copy All Request/Response'}
              </button>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Endpoint</h3>
              <div className={styles.endpointBox}>
                <code>{event.endpoint || 'Not recorded'}</code>
              </div>
            </div>

            {event.request_headers && Object.keys(event.request_headers).length > 0 && (
              <div className={styles.section}>
                <div 
                  className={styles.collapsibleHeader}
                  onClick={() => setIsHeadersExpanded(!isHeadersExpanded)}
                >
                  <h3 className={styles.sectionTitle}>
                    <span className={styles.collapseIcon}>{isHeadersExpanded ? '▼' : '▶'}</span>
                    Request Headers
                    <span className={styles.headerCount}>({Object.keys(event.request_headers).length})</span>
                  </h3>
                  {isHeadersExpanded && (
                    <button 
                      className={styles.copyButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(
                          Object.entries(event.request_headers!)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join('\n'),
                          'headers'
                        );
                      }}
                    >
                      {copiedField === 'headers' ? '✓ Copied!' : '📋 Copy'}
                    </button>
                  )}
                </div>
                {isHeadersExpanded && (
                  <div className={styles.headersTable}>
                    {Object.entries(event.request_headers).map(([key, value]) => (
                      <div key={key} className={styles.headerRow}>
                        <span className={styles.headerKey}>{key}</span>
                        <span className={styles.headerValue}>{value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Request Summary</h3>
                {event.request_summary && (
                  <button 
                    className={styles.copyButton}
                    onClick={() => copyToClipboard(JSON.stringify(event.request_summary, null, 2), 'request')}
                  >
                    {copiedField === 'request' ? '✓ Copied!' : '📋 Copy'}
                  </button>
                )}
              </div>
              {event.request_summary ? (
                <div className={styles.jsonBox}>
                  <pre>{JSON.stringify(event.request_summary, null, 2)}</pre>
                </div>
              ) : (
                <p className={styles.noContent}>No request summary available.</p>
              )}
            </div>

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Response Summary</h3>
                {event.response_summary && (
                  <button 
                    className={styles.copyButton}
                    onClick={() => copyToClipboard(JSON.stringify(event.response_summary, null, 2), 'response')}
                  >
                    {copiedField === 'response' ? '✓ Copied!' : '📋 Copy'}
                  </button>
                )}
              </div>
              {event.response_summary ? (
                <div className={styles.jsonBox}>
                  <pre>{JSON.stringify(event.response_summary, null, 2)}</pre>
                </div>
              ) : (
                <p className={styles.noContent}>No response summary available.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'session' && (
          <div className={styles.sessionTab}>
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Session Timeline</h3>
                {sessionEvents.length > 0 && (
                  <button 
                    className={styles.copyAllButton}
                    disabled={isCopyingSession}
                    onClick={async () => {
                      setIsCopyingSession(true);
                      setCopiedField('session-loading');
                      
                      try {
                        // Fetch full details for each event
                        const fullEvents = await Promise.all(
                          sessionEvents.map(e => getSyncEvent(e.id).catch(() => e))
                        );
                        
                        const sortedEvents = [...fullEvents].sort(
                          (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
                        );
                        
                        const sessionText = `SESSION INFO
============
Session ID: ${event.session_id}
User Email: ${event.user_email || '-'}
Device Code: ${event.device_code || '-'}
Device Name: ${event.device_name || '-'}
Firmware Version: ${event.firmware_version || '-'}
Total Events: ${sortedEvents.length}

${'='.repeat(80)}
SESSION EVENTS
${'='.repeat(80)}

${sortedEvents.map((e, idx) => {
  const quarantineSection = e.was_quarantined 
    ? `
🔒 QUARANTINE STATUS
--------------------
Was Quarantined: Yes
Reason: ${e.quarantine_reason ? QUARANTINE_REASON_LABELS[e.quarantine_reason] : 'Unknown'}
` : '';

  const corruptionSection = e.has_suspicious_chars
    ? `
⚠️ CORRUPTION DETECTED
----------------------
Suspicious Characters: ${e.suspicious_char_count?.toLocaleString() || 'Unknown'}
Details: ${e.suspicious_details || 'No details'}
` : '';

  const headersSection = e.request_headers && Object.keys(e.request_headers).length > 0
    ? `
REQUEST HEADERS
---------------
${Object.entries(e.request_headers).map(([k, v]) => `${k}: ${v}`).join('\n')}
` : '';

  return `
${'─'.repeat(80)}
EVENT ${idx + 1} OF ${sortedEvents.length}
${'─'.repeat(80)}

EVENT INFO
----------
Event ID: ${e.id}
Event Type: ${e.event_type}
Timestamp: ${e.timestamp}
Firmware Version: ${e.firmware_version || '-'}
File Name: ${e.file_name || '-'}
File ID: ${e.file_id || '-'}
Project: ${e.project_name || '-'}
Status: ${e.success ? 'Success' : 'Failed'}
${quarantineSection}${corruptionSection}${formatRawBytesDiagnostic(e)}
ENDPOINT
--------
${e.endpoint || 'Not recorded'}
${headersSection}

REQUEST SUMMARY
---------------
${e.request_summary ? JSON.stringify(e.request_summary, null, 2) : '(No request summary)'}

RESPONSE SUMMARY
----------------
${e.response_summary ? JSON.stringify(e.response_summary, null, 2) : '(No response summary)'}

RAW CONTENT (FROM DEVICE)
-------------------------
${e.content_raw || '(No raw content)'}

PROCESSED CONTENT (AFTER SERVER PROCESSING)
--------------------------------------------
${e.content_processed || '(No processed content)'}
`;
}).join('\n')}`;

                        await navigator.clipboard.writeText(sessionText);
                        setCopiedField('session');
                        setTimeout(() => setCopiedField(null), 2000);
                      } catch (err) {
                        console.error('Failed to copy session:', err);
                        setCopiedField(null);
                      } finally {
                        setIsCopyingSession(false);
                      }
                    }}
                  >
                    {copiedField === 'session-loading' ? '⏳ Loading...' : copiedField === 'session' ? '✓ Copied!' : '📋 Copy Entire Session'}
                  </button>
                )}
              </div>
              <p className={styles.sessionInfo}>
                Session ID: <code>{event.session_id}</code>
              </p>
              
              {sessionEvents.length === 0 ? (
                <p className={styles.noContent}>No other events found in this session.</p>
              ) : (
                <div className={styles.timeline}>
                  {sessionEvents
                    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                    .map((sessionEvent, idx) => (
                      <div 
                        key={sessionEvent.id} 
                        className={`${styles.timelineItem} ${sessionEvent.id === event.id ? styles.currentEvent : ''}`}
                      >
                        <div className={styles.timelineMarker}>
                          <span className={styles.timelineNumber}>{idx + 1}</span>
                        </div>
                        <div className={styles.timelineContent}>
                          <div className={styles.timelineHeader}>
                            <span className={styles.timelineIcon}>{getEventTypeIcon(sessionEvent.event_type)}</span>
                            <span className={styles.timelineType}>{getEventTypeLabel(sessionEvent.event_type)}</span>
                            {sessionEvent.has_suspicious_chars && (
                              <span className={styles.timelineCorrupted}>⚠️</span>
                            )}
                            {!sessionEvent.success && (
                              <span className={styles.timelineFailed}>❌</span>
                            )}
                          </div>
                          <div className={styles.timelineMeta}>
                            <span>{formatTimestamp(sessionEvent.timestamp)}</span>
                            {sessionEvent.file_name && (
                              <>
                                <span className={styles.separator}>•</span>
                                <span>{sessionEvent.file_name}</span>
                              </>
                            )}
                          </div>
                          {sessionEvent.id !== event.id && (
                            <Link 
                              to={`/events/${sessionEvent.id}`} 
                              className={styles.timelineLink}
                            >
                              View details →
                            </Link>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

